import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { PlatformConditionCharts } from '../PlatformConditionCharts';
import { UnitsEnum, useAnalyticsQuery } from '@/__generated__/graphql';
import { unitSystem as unitSystemState } from '@/components/Analysis/state';
import { localiseMeasurement } from '@/utils/unitSystem';

/**
 * Container component for Platform Condition Charts
 * Handles data fetching and state management for analytics data
 */
type Props = {
  activeDeploymentIds: string[];
};

// Simple API response types matching the provided structure
type RiskData = {
  total: number;
  remediatedTotal: number;
};

type MetricConfig = {
  name: string;
  key: string;
  color: string;
};

type DisplayConfig = {
  title: string;
  chartType: string;
  internalMetrics?: {
    key: string;
    name: string;
    color: string;
  };
  metrics: MetricConfig[];
};

type AnalyticsResponse = {
  data: Record<string, RiskData>;
  id: string;
  structureIds: string[];
  type: 'CONDITION_BY_COATING' | 'CONDITION_BY_CORROSION';
  displayConfig?: DisplayConfig;
};

// Type guard to check if an item is a valid analytics response
const isAnalyticsResponse = (item: unknown): item is AnalyticsResponse => {
  if (!item || typeof item !== 'object') return false;
  const i = item as Partial<AnalyticsResponse>;
  return (
    !!i &&
    typeof i.id === 'string' &&
    Array.isArray(i.structureIds) &&
    (i.type === 'CONDITION_BY_COATING' || i.type === 'CONDITION_BY_CORROSION') &&
    !!i.data &&
    typeof i.data === 'object'
  );
};

// Define the return type for processData
type ProcessedData = {
  byType: {
    CONDITION_BY_COATING: {
      data: Record<string, { total: number; remediatedTotal: number }>;
      displayConfig: DisplayConfig | null;
      colorMap: Record<string, string>;
      title: string;
    };
    CONDITION_BY_CORROSION: {
      data: Record<string, { total: number; remediatedTotal: number }>;
      displayConfig: DisplayConfig | null;
      colorMap: Record<string, string>;
      title: string;
    };
  };
  getChartData: (type: 'CONDITION_BY_COATING' | 'CONDITION_BY_CORROSION') => Array<{
    key: string;
    count: number;
    remediated: number;
    color: string;
  }>;
  hasMeaningfulData: (type: 'CONDITION_BY_COATING' | 'CONDITION_BY_CORROSION') => boolean;
  getLegendConfig: (type: 'CONDITION_BY_COATING' | 'CONDITION_BY_CORROSION') => Array<{
    key: string;
    name: string;
    color: string;
    isInternal?: boolean;
  }>;
  getTitle: (type: 'CONDITION_BY_COATING' | 'CONDITION_BY_CORROSION') => string;
};

// Process analytics data and return aggregated results by type
const processData = (
  analyticsData: AnalyticsResponse[],
  unitSystem: ReturnType<typeof useAtomValue<typeof unitSystemState>>
): ProcessedData => {
  const result: ProcessedData = {
    byType: {
      CONDITION_BY_COATING: {
        data: {},
        displayConfig: null,
        colorMap: {},
        title: '',
      },
      CONDITION_BY_CORROSION: {
        data: {},
        displayConfig: null,
        colorMap: {},
        title: '',
      },
    },
    getChartData: function (type) {
      const typeData = this.byType[type];
      if (!typeData.displayConfig?.metrics) return [];
      return typeData.displayConfig.metrics
        .filter((metric) => metric.key && typeData.data[metric.key])
        .map((metric) => ({
          key: metric.key,
          count: Number(
            localiseMeasurement({
              unitSystem,
              sourceUnit: UnitsEnum.SquareMeter,
              value: typeData.data[metric.key].total,
              showUnitLabel: false,
              roundToDecimal: 2,
            })
          ),
          remediated: Number(
            localiseMeasurement({
              unitSystem,
              sourceUnit: UnitsEnum.SquareMeter,
              value: typeData.data[metric.key].remediatedTotal,
              showUnitLabel: false,
              roundToDecimal: 2,
            })
          ),
          color: metric.color,
        }));
    },
    hasMeaningfulData: function (type) {
      const chartData = this.getChartData(type);
      return chartData.length > 0 && chartData.some((item) => item.count > 0);
    },
    getLegendConfig: function (type) {
      const analyticsItem = analyticsData.find((item) => item.type === type);
      if (!analyticsItem?.displayConfig) return [];
      const mainMetrics = (analyticsItem.displayConfig.metrics || [])
        .filter((metric) => metric && metric.key)
        .map((metric) => ({
          key: String(metric.key),
          name: String(metric.name || metric.key),
          color: String(metric.color || '#607D8B'),
          isInternal: false,
        }));
      const internalMetrics = analyticsItem.displayConfig.internalMetrics
        ? [
            {
              key: String(analyticsItem.displayConfig.internalMetrics.key || 'internal'),
              name: String(analyticsItem.displayConfig.internalMetrics.name || 'Internal'),
              color: String(analyticsItem.displayConfig.internalMetrics.color || '#9E9E9E'),
              isInternal: true,
            },
          ]
        : [];
      return [...mainMetrics, ...internalMetrics];
    },
    getTitle: function (type) {
      return this.byType[type].displayConfig?.title || '';
    },
  };

  // Process each analytics item
  analyticsData.forEach((item) => {
    if (!isAnalyticsResponse(item)) return;
    const type = item.type;
    const typeData = result.byType[type];

    if (item.displayConfig && !typeData.displayConfig) {
      typeData.displayConfig = item.displayConfig;
      typeData.title = item.displayConfig.title;
      item.displayConfig.metrics.forEach((metric) => {
        typeData.colorMap[metric.key] = metric.color;
      });
    }

    Object.entries(item.data).forEach(([key, riskData]) => {
      if (!typeData.data[key]) {
        typeData.data[key] = { total: 0, remediatedTotal: 0 };
      }
      typeData.data[key].total += riskData?.total || 0;
      typeData.data[key].remediatedTotal += riskData?.remediatedTotal || 0;
    });
  });

  return result;
};

export function PlatformConditionChartsContainer({ activeDeploymentIds }: Props) {
  const unitSystem = useAtomValue(unitSystemState);
  const { data, loading, error } = useAnalyticsQuery({
    variables: {
      input: {
        structureIds: activeDeploymentIds,
        type: ['CONDITION_BY_COATING', 'CONDITION_BY_CORROSION'],
      },
    },
    skip: !activeDeploymentIds?.length,
    fetchPolicy: 'cache-first',
  });

  const processedData = useMemo<ProcessedData>(() => {
    if (!data?.analytics) {
      return {
        byType: {
          CONDITION_BY_COATING: { data: {}, displayConfig: null, colorMap: {}, title: '' },
          CONDITION_BY_CORROSION: { data: {}, displayConfig: null, colorMap: {}, title: '' },
        },
        getChartData: () => [],
        hasMeaningfulData: () => false,
        getLegendConfig: () => [],
        getTitle: () => '',
      };
    }
    try {
      // Cast is safe due to server schema; guard remains in processData
      return processData(data.analytics as unknown as AnalyticsResponse[], unitSystem);
    } catch (e) {
      return {
        byType: {
          CONDITION_BY_COATING: { data: {}, displayConfig: null, colorMap: {}, title: '' },
          CONDITION_BY_CORROSION: { data: {}, displayConfig: null, colorMap: {}, title: '' },
        },
        getChartData: () => [],
        hasMeaningfulData: () => false,
        getLegendConfig: () => [],
        getTitle: () => 'Error loading data',
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeDeploymentIds, unitSystem]);

  if (loading) {
    return <div>Loading analytics data...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        Error loading analytics data. Please try again later.
      </div>
    );
  }

  const corrosionChartData = processedData.getChartData('CONDITION_BY_CORROSION');
  const coatingsChartData = processedData.getChartData('CONDITION_BY_COATING');
  const corrosionLegendConfig = processedData.getLegendConfig('CONDITION_BY_CORROSION');
  const coatingsLegendConfig = processedData.getLegendConfig('CONDITION_BY_COATING');
  const corrosionTitle = processedData.getTitle('CONDITION_BY_CORROSION');
  const coatingsTitle = processedData.getTitle('CONDITION_BY_COATING');

  // Check if either chart has meaningful data (non-zero values)
  const hasCorrosionData = processedData.hasMeaningfulData('CONDITION_BY_CORROSION');
  const hasCoatingsData = processedData.hasMeaningfulData('CONDITION_BY_COATING');

  return (
    <>
      {hasCorrosionData || hasCoatingsData ? (
        <PlatformConditionCharts
          corrosionData={corrosionChartData}
          coatingsData={coatingsChartData}
          loading={loading}
          corrosionTitle={corrosionTitle}
          coatingsTitle={coatingsTitle}
          corrosionLegendConfig={corrosionLegendConfig}
          coatingsLegendConfig={coatingsLegendConfig}
        />
      ) : (
        <></>
      )}
    </>
  );
}
